import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Share2, Check, GitCompareArrows, Gavel, ExternalLink, Plus, Users } from "lucide-react";
import { FollowUpChat } from "./FollowUpChat";
import { TranslateControl } from "./TranslateControl";
import { SubmitReferenceModal } from "./SubmitReferenceModal";
import { UnifiedCognitivePanel } from "./UnifiedCognitivePanel";
import { ExportControl } from "./ExportControl";
import { exportDocument } from "@/lib/exportDocument";
import { getApprovedReferences } from "@/lib/rexxponsApi";

const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

const SectionHeading = ({ index, icon: Icon, children }) => (
  <div className="flex items-center gap-3 mb-6">
    <span className="font-sans text-xs text-primary tracking-[0.2em]">0{index}</span>
    <Icon strokeWidth={1.5} className="w-4 h-4 text-primary" />
    <h2 className="font-heading font-medium text-2xl tracking-tight">{children}</h2>
  </div>
);

export const CrossRefDocument = ({ doc, onBack, onShare }) => {
  const [current, setCurrent] = useState(doc);
  const [approved, setApproved] = useState([]);
  const [refOpen, setRefOpen] = useState(false);
  useEffect(() => { setCurrent(doc); }, [doc]);
  useEffect(() => { getApprovedReferences(doc.id).then(setApproved); }, [doc.id]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="relative z-10 min-h-screen w-full pt-24 pb-32 px-5 sm:px-8 max-w-5xl mx-auto"
    >
      <div className="flex items-center justify-between mb-6">
        <button
          data-testid="crossref-back-btn"
          onClick={onBack}
          className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft strokeWidth={1.5} className="w-4 h-4" /> New
        </button>
        <div className="flex items-center gap-2">
          <ExportControl
            testid="crossref-export-btn"
            variant="pill"
            onExport={(lang) => exportDocument(doc, "crossref", { lang })}
            hasUnified={!!doc.unified_output}
          />
          <button
            data-testid="crossref-share-btn"
            onClick={() => onShare?.(`${current.title}\n\n${current.summary}\n\nCross-referenced with Rexxpons`)}
            className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-muted-foreground hover:text-primary border border-border hover:border-primary/40 rounded-full px-3 py-1.5 transition-colors"
          >
            <Share2 strokeWidth={1.5} className="w-3.5 h-3.5" /> Share
          </button>
        </div>
      </div>

      <p className="font-sans text-xs tracking-[0.25em] uppercase text-muted-foreground mb-4">
        Rexxpons · Cross-Reference Report · {current.sources.length} sources
      </p>
      <h1 data-testid="crossref-title" className="font-heading font-light text-4xl sm:text-5xl tracking-tight leading-none mb-6">
        {current.title}
      </h1>

      <div className="flex flex-wrap items-center gap-3 mb-10" data-testid="crossref-tools">
        <TranslateControl docId={doc.id} docKind="crossref" original={doc} onTranslated={setCurrent} />
        <button
          data-testid="crossref-add-source-btn"
          onClick={() => setRefOpen(true)}
          className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-muted-foreground hover:text-primary transition-colors border border-border hover:border-primary/40 rounded-full px-3 py-1.5"
        >
          <Plus strokeWidth={1.8} className="w-3.5 h-3.5" /> Contribute a source
        </button>
      </div>

      <p className="font-heading text-xl sm:text-2xl font-light leading-snug text-foreground/90 mb-16">{current.summary}</p>

      {/* Agreements */}
      <section className="mb-16">
        <SectionHeading index={1} icon={Check}>Points of Agreement</SectionHeading>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {current.agreements.map((a, i) => (
            <div key={`${i}-${a.slice(0, 16)}`} data-testid="crossref-agreement" className="flex items-start gap-3 bg-secondary/50 border border-border rounded-lg p-4">
              <Check strokeWidth={2} className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <p className="text-sm leading-relaxed text-foreground/90">{a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Frictions */}
      <section className="mb-16">
        <SectionHeading index={2} icon={GitCompareArrows}>Friction Points</SectionHeading>
        <div className="space-y-6">
          {current.frictions.map((f, i) => (
            <div key={`${i}-${f.point.slice(0, 16)}`} data-testid="crossref-friction" className="border border-border rounded-xl overflow-hidden">
              <div className="bg-secondary/70 px-5 py-3 border-b border-border">
                <p className="font-heading text-base text-foreground">{f.point}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
                {f.positions.map((p, j) => (
                  <div key={`${j}-${p.source.slice(0, 12)}`} className="p-4">
                    <p className="text-[11px] tracking-[0.12em] uppercase text-primary mb-1.5">{p.source}</p>
                    <p className="text-sm leading-relaxed text-foreground/80">{p.stance}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Verdict */}
      <section className="mb-16">
        <SectionHeading index={3} icon={Gavel}>Forensic Verdict</SectionHeading>
        <div className="border-l-2 border-primary/50 pl-5">
          <p className="font-heading text-lg leading-relaxed text-foreground/90">{current.verdict}</p>
        </div>
      </section>

      {/* Sources */}
      <section>
        <SectionHeading index={4} icon={ExternalLink}>Cross-Referenced Sources</SectionHeading>
        <ul className="divide-y divide-border border-t border-b border-border">
          {current.sources.map((s, i) => (
            <li key={`${i}-${s.url}`} data-testid="crossref-source">
              <a href={s.url} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-between gap-4 py-4 hover:bg-secondary/40 -mx-2 px-2 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <img src={`https://www.google.com/s2/favicons?domain=${hostOf(s.url)}&sz=64`} alt="" className="w-5 h-5 rounded shrink-0" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                  <div className="min-w-0">
                    <p className="text-sm text-foreground truncate">{s.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{hostOf(s.url)}</p>
                  </div>
                </div>
                <ExternalLink strokeWidth={1.5} className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
              </a>
            </li>
          ))}
        </ul>

        {approved.length > 0 && (
          <div className="mt-8" data-testid="crossref-community-refs">
            <div className="flex items-center gap-2.5 mb-4">
              <Users strokeWidth={1.5} className="w-4 h-4 text-primary" />
              <span className="font-sans text-xs tracking-[0.18em] uppercase text-primary">Community References</span>
            </div>
            <ul className="divide-y divide-border border-t border-b border-border">
              {approved.map((r, i) => (
                <li key={`${i}-${r.url}`} data-testid="crossref-community-ref-item">
                  <a href={r.url} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-between gap-4 py-4 hover:bg-secondary/40 -mx-2 px-2 transition-colors">
                    <div className="min-w-0">
                      <p className="text-sm text-foreground truncate">{r.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{r.note || r.url}</p>
                    </div>
                    <ExternalLink strokeWidth={1.5} className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {doc.unified_output && <UnifiedCognitivePanel unified={doc.unified_output} />}

      <FollowUpChat docId={current.id} docKind="crossref" />

      <SubmitReferenceModal open={refOpen} onClose={() => setRefOpen(false)} docId={doc.id} docKind="crossref" />
    </motion.div>
  );
};
